'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import InputField from '@/components/common/InputField/InputField';
import { createClass, updateClass, Class } from '@/actions/data-fetch-classes';
import { toastMessage } from '@/utils/toast-message';

// Form validation schema
const ClassFormSchema = z.object({
  class_name: z.string().min(2, 'Class name is required').max(100),
  section: z.string().optional().default(''),
  max_strength: z.coerce.number().int().positive('Max strength must be a positive number').optional(),
  description: z.string().optional().default('')
});

type ClassFormData = z.infer<typeof ClassFormSchema>;

interface ModernClassFormProps {
  initialData?: Class | null;
  onSuccess: (data: Class) => void;
  onClose: () => void;
}

const ModernClassForm: React.FC<ModernClassFormProps> = ({ initialData, onSuccess, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!initialData;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ClassFormData>({
    resolver: zodResolver(ClassFormSchema),
    defaultValues: {
      class_name: initialData?.class_name || '',
      section: initialData?.section || '',
      max_strength: initialData?.max_strength || undefined,
      description: initialData?.description || ''
    }
  });

  const onSubmit = async (formData: ClassFormData) => {
    try {
      setIsSubmitting(true);

      let response;
      if (isEditing && initialData) {
        response = await updateClass(initialData.id, {
          class_name: formData.class_name,
          section: formData.section,
          max_strength: formData.max_strength,
          description: formData.description
        });
      } else {
        response = await createClass({
          class_name: formData.class_name,
          section: formData.section,
          max_strength: formData.max_strength,
          description: formData.description
        });
      }

      if (response?.success) {
        toastMessage(
          isEditing ? 'Class updated successfully' : 'Class created successfully',
          'success'
        );
        onSuccess(response.data);
        reset();
        onClose();
      } else {
        toastMessage(response?.message || 'Operation failed', 'error');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Something went wrong', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6 max-w-2xl">
            {/* Class Name */}
            <InputField
              label="Class Name"
              id="class_name"
              name="class_name"
              control={control}
              required
              error={errors.class_name?.message}
              placeholder="e.g., Class 10A"
            />

            {/* Section */}
            <InputField
              label="Section"
              id="section"
              name="section"
              control={control}
              placeholder="e.g., A, B, C"
              error={errors.section?.message}
            />

            {/* Max Strength */}
            <InputField
              label="Max Strength"
              id="max_strength"
              name="max_strength"
              control={control}
              type="number"
              placeholder="e.g., 45"
              error={errors.max_strength?.message}
            />

            {/* Description */}
            <InputField
              label="Description"
              id="description"
              name="description"
              control={control}
              placeholder="Optional description"
              error={errors.description?.message}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-brandColor text-white hover:bg-brandColor/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEditing ? 'Update Class' : 'Create Class'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ModernClassForm;
